import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useNavigate } from 'react-router-dom';
import { PersonAdd, Security, Build, Assessment } from '@mui/icons-material';
import { StockService } from '../services/stockService';
import { collection, getDocs, updateDoc, doc, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { format } from 'date-fns';
import FixPositionDates from '../components/FixPositionDates';
import TestVolumeData from '../components/TestVolumeData';
import MigrationRunner from '../components/MigrationRunner';
import VolumeDebug from '../components/VolumeDebug';
import { fixAvailableFunds } from '../utils/fixAvailableFunds';

const Admin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [stocksToFix, setStocksToFix] = useState<any[]>([]);
  const [apiUsage, setApiUsage] = useState<{
    today: number;
    thisMonth: number;
    lastUpdated: Date | null;
  }>({ today: 0, thisMonth: 0, lastUpdated: null });
  const [fixingTotals, setFixingTotals] = useState(false);
  const [fixTotalsMessage, setFixTotalsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load API usage stats on component mount
  React.useEffect(() => {
    loadApiUsageStats();
  }, []);

  const loadApiUsageStats = async () => {
    try {
      // Get today's API logs
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const apiLogsRef = collection(db, 'apiUsageLogs');
      const todayQuery = query(
        apiLogsRef,
        where('timestamp', '>=', today),
        orderBy('timestamp', 'desc')
      );
      
      const todaySnapshot = await getDocs(todayQuery);
      const todayUsage = todaySnapshot.size;
      
      // Get this month's usage (estimated)
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthQuery = query(
        apiLogsRef,
        where('timestamp', '>=', firstOfMonth)
      );
      
      const monthSnapshot = await getDocs(monthQuery);
      const monthUsage = monthSnapshot.size;
      
      // Get last update time
      const lastUpdateQuery = query(
        apiLogsRef,
        orderBy('timestamp', 'desc'),
        limit(1)
      );
      
      const lastUpdateSnapshot = await getDocs(lastUpdateQuery);
      const lastUpdated = !lastUpdateSnapshot.empty 
        ? lastUpdateSnapshot.docs[0].data().timestamp?.toDate() 
        : null;
      
      setApiUsage({
        today: todayUsage,
        thisMonth: monthUsage,
        lastUpdated
      });
    } catch (error) {
      console.error('Error loading API usage stats:', error);
    }
  };

  const checkMissingPriceWhenAdded = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'stocks'));
      const stocks: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (!data.priceWhenAdded && data.currentPrice) {
          stocks.push({
            id: doc.id,
            symbol: doc.id,
            currentPrice: data.currentPrice,
            priceWhenAdded: data.priceWhenAdded || 'Not Set',
          });
        }
      });
      
      setStocksToFix(stocks);
      setMessage({ 
        type: 'success', 
        text: `Found ${stocks.length} stocks missing priceWhenAdded` 
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const fixPriceWhenAdded = async () => {
    setLoading(true);
    try {
      let fixed = 0;
      
      for (const stock of stocksToFix) {
        const stockRef = doc(db, 'stocks', stock.symbol);
        await updateDoc(stockRef, {
          priceWhenAdded: stock.currentPrice
        });
        fixed++;
      }
      
      setMessage({ 
        type: 'success', 
        text: `Fixed ${fixed} stocks - set priceWhenAdded to current price` 
      });
      setStocksToFix([]);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const navigate = useNavigate();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Admin Tools
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardActionArea onClick={() => navigate('/admin/beta-access')}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonAdd sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Beta Access</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Manage beta user access list and permissions
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardActionArea onClick={() => navigate('/admin/usage')}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Assessment sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Usage Dashboard</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Monitor user activity, portfolios, and system metrics
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Data Cleanup Tools
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Fix stocks missing "Price When Added" by setting it to current price
          </Typography>
          <Button
            variant="contained"
            onClick={checkMissingPriceWhenAdded}
            disabled={loading}
            sx={{ mr: 2 }}
          >
            Check Missing Data
          </Button>
          
          {stocksToFix.length > 0 && (
            <Button
              variant="contained"
              color="warning"
              onClick={fixPriceWhenAdded}
              disabled={loading}
            >
              Fix {stocksToFix.length} Stocks
            </Button>
          )}
        </Box>

        {message && (
          <Alert severity={message.type} sx={{ mt: 2 }}>
            {message.text}
          </Alert>
        )}

        {stocksToFix.length > 0 && (
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Current Price</TableCell>
                  <TableCell>Price When Added</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stocksToFix.map((stock) => (
                  <TableRow key={stock.symbol}>
                    <TableCell>{stock.symbol}</TableCell>
                    <TableCell>${stock.currentPrice}</TableCell>
                    <TableCell>{stock.priceWhenAdded}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Automated Price Updates Strategy
        </Typography>
        <Typography variant="body2" paragraph>
          For regular price updates, consider these options:
        </Typography>
        <ol>
          <li>
            <strong>Firebase Scheduled Functions</strong> - Run price updates every 15-30 minutes during market hours
          </li>
          <li>
            <strong>Alpha Vantage API</strong> - Free tier allows 25 requests/day (could update all stocks twice daily)
          </li>
          <li>
            <strong>Manual Trigger</strong> - Add "Update All Prices" button that fetches real-time data
          </li>
          <li>
            <strong>Import-Based</strong> - Continue using MarketSurge exports as primary update mechanism
          </li>
        </ol>
        <Typography variant="body2" color="textSecondary">
          Recommended: Combination of scheduled updates (2x daily) + manual refresh + import updates
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Fix Position Entry Dates
        </Typography>
        <FixPositionDates />
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Fix Portfolio Totals
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Recalculates totalEarnings and availableFunds for all portfolios from closed positions (source of truth).
          This fixes any drift caused by the autopilot bug or other inconsistencies.
        </Typography>
        {fixTotalsMessage && (
          <Alert severity={fixTotalsMessage.type} sx={{ mb: 2 }} onClose={() => setFixTotalsMessage(null)}>
            {fixTotalsMessage.text}
          </Alert>
        )}
        <Button
          variant="contained"
          color="warning"
          disabled={fixingTotals}
          onClick={async () => {
            setFixingTotals(true);
            setFixTotalsMessage(null);
            try {
              await fixAvailableFunds();
              setFixTotalsMessage({ type: 'success', text: 'Portfolio totals fixed. Check browser console for details.' });
            } catch (err) {
              setFixTotalsMessage({ type: 'error', text: `Error: ${err}` });
            } finally {
              setFixingTotals(false);
            }
          }}
        >
          {fixingTotals ? 'Fixing...' : 'Fix All Portfolio Totals'}
        </Button>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Twelve Data API Usage
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Today's Usage:</strong> {apiUsage.today} / 800 credits
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>This Month:</strong> {apiUsage.thisMonth} credits (estimated)
          </Typography>
          {apiUsage.lastUpdated && (
            <Typography variant="caption" color="textSecondary">
              Last API call: {format(apiUsage.lastUpdated, 'MMM dd, yyyy h:mm a')}
            </Typography>
          )}
        </Box>
        
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>API Limits (Grow Plan):</strong>
          </Typography>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>800 API credits per day</li>
            <li>Each price update uses 1 credit per symbol</li>
            <li>Batch requests (up to 120 symbols) count as 1 credit</li>
            <li>Scheduled updates: 2x daily during market hours</li>
          </ul>
        </Alert>
        
        <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
          Note: API usage tracking is estimated based on function calls. For accurate usage, check your Twelve Data dashboard.
        </Typography>
      </Paper>

      <TestVolumeData />

      <VolumeDebug symbol="LCII" />

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Shared Watchlist Migration
        </Typography>
        <MigrationRunner />
      </Paper>
    </Box>
  );
};

export default Admin;